import Link from "next/link";
import { Eye, Lock, Shield, Flame } from "lucide-react";

function fv(n: number): string { if (n >= 1e6) return `${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `${(n/1e3).toFixed(1)}K`; return n.toString(); }
function fd(s: number): string { const m = Math.floor(s/60), sc = s%60; return `${m}:${sc.toString().padStart(2,"0")}`; }
function ft(d: Date|string): string { const diff = Date.now()-new Date(d).getTime(); const s=Math.floor(diff/1000),mi=Math.floor(s/60),h=Math.floor(mi/60),dy=Math.floor(h/24); if(s<60)return "just now";if(mi<60)return `${mi}m ago`;if(h<24)return `${h}h ago`;if(dy<30)return `${dy}d ago`;return `${Math.floor(dy/30)}mo ago`; }

type Props = { content: any; variant?: "grid"|"compact"; index?: number };

export default function ContentCard({ content, variant = "grid", index = 0 }: Props) {
  const isLocked = content.access !== "FREE";
  const isHot = content.views > 50000;

  if (variant === "compact") {
    return (
      <Link href={`/watch/${content.id}`} className="group flex gap-3 hover:bg-white/[0.02] rounded transition-colors">
        <div className="relative w-40 aspect-video shrink-0 overflow-hidden rounded bg-neutral-900">
          {content.thumbnailUrl ? <img src={content.thumbnailUrl} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-neutral-800" />}
          {content.duration && <span className="absolute bottom-1 right-1 bg-black/80 text-[10px] font-bold text-white px-1 py-0.5 rounded text-center font-mono">{fd(content.duration)}</span>}
        </div>
        <div className="flex-1 min-w-0 py-1">
          <h3 className="text-xs font-semibold text-neutral-200 group-hover:text-white line-clamp-2 mb-1 leading-tight">{content.title}</h3>
          {content.creator && <div className="text-[10px] text-neutral-500">{content.creator.username}</div>}
          <div className="text-[10px] text-neutral-600 mt-0.5">{fv(content.views)} views</div>
        </div>
      </Link>
    );
  }

  return (
    <Link href={`/watch/${content.id}`} className="group block">
      {/* THUMBNAIL */}
      <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-neutral-900 mb-2">
        {content.thumbnailUrl ? (
          <img
            src={content.thumbnailUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-neutral-900 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center">
              <div className="w-0 h-0 border-l-[12px] border-l-white/30 border-y-[7px] border-y-transparent ml-1" />
            </div>
          </div>
        )}

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

        {/* Duration */}
        {content.duration && (
          <span className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-[10px] font-bold font-mono px-1.5 py-0.5 rounded z-10">
            {fd(content.duration)}
          </span>
        )}

        {/* Lock badge */}
        {isLocked && (
          <span className={`absolute top-1.5 left-1.5 flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-black uppercase z-10 ${
            content.access === "PPV"
              ? "badge-ppv"
              : "badge-sub"
          }`}>
            <Lock className="w-2.5 h-2.5" />
            {content.access === "PPV" ? `${content.ppvPrice}` : "VIS"}
          </span>
        )}

        {/* Hot badge */}
        {isHot && !isLocked && (
          <span className="absolute top-1.5 left-1.5 badge-hot flex items-center gap-1 z-10">
            <Flame className="w-2.5 h-2.5" /> HOT
          </span>
        )}

        {/* Hover red tint */}
        <div className="absolute inset-0 bg-red-600/0 group-hover:bg-red-600/10 transition-colors duration-300 pointer-events-none z-[3]" />
      </div>

      {/* INFO — PH style: title, then creator + views on one line */}
      <h3 className="text-[13px] font-semibold text-neutral-200 group-hover:text-white leading-snug line-clamp-2 mb-1 transition-colors">
        {content.title}
      </h3>
      <div className="flex items-center gap-2 text-[11px] text-neutral-500">
        {content.creator && (
          <>
            {content.creator.avatarUrl ? (
              <img src={content.creator.avatarUrl} alt="" className="w-4 h-4 rounded-full object-cover" />
            ) : (
              <div className="w-4 h-4 rounded-full bg-neutral-800 flex items-center justify-center text-[7px] font-bold text-neutral-400">{content.creator.username[0]}</div>
            )}
            <span className="group-hover:text-red-400 transition-colors truncate">{content.creator.username}</span>
            {content.creator.isVerified && <Shield className="w-3 h-3 text-blue-500 shrink-0" />}
            <span className="text-neutral-700">|</span>
          </>
        )}
        <span className="flex items-center gap-0.5"><Eye className="w-3 h-3 text-neutral-600" />{fv(content.views)}</span>
        <span className="text-neutral-700">|</span>
        <span>{ft(content.createdAt)}</span>
      </div>
    </Link>
  );
}
